<template>
  <div
    class="message"
    :data-id="message.id"
    :data-read="message.read ? 'true' : 'false'"
    :class="[isIncoming ? 'message--incoming' : 'message--outcoming', message.isGhost && 'is-ghost', {'message--selected': selectMode }]"
  >
    <div class="message__user" v-if="message.author">
      <img :src="message.author.avatar" alt="" />
    </div>
    <div
      class="message__wrapper"
      :class="[isFile && 'is-file', {'message__wrapper--forward': forward}]"
      @click="closeOptions"
      @contextmenu.prevent="openOptions"
      v-click-outside="{events: ['click', 'contextmenu', 'wheel'], handler: closeOptions}"
    >
      <div class="message__select">
        <Checkbox :value="selected" />
      </div>
      <div class="message__forward" v-if="forward">
        Forward from: <span>{{ forward.author.first_name }} {{ forward.author.last_name }}</span>
      </div>
      <div
        v-if="quote"
        class="message__quote"
      >
        <div class="message__quote-name">{{ quote.author.first_name }} {{ quote.author.last_name }}</div>
        <div class="message__quote-content" v-html="quote.content"></div>
      </div>
      <div
        v-if="message.content"
        ref="content"
        class="message__content"
        :class="[isIncoming && 'dark', isFile && 'is-file']"
        v-html="messageBody"
      />

      <template v-if="message.file">
        <div class="message__file" v-if="message.type.id === 2" @click="handleFileClick">
          <div v-viewer class="message__file-image">
            <img :src="fileImageUrl" :alt="message.file.name" />
          </div>
        </div>
        <div class="message__file" v-else-if="message.type.id === 3" @click="handleFileClick">
          <div class="message__file-icon">
            <SvgIcon name="file" />
          </div>
          <div class="message__file-meta">
            <div class="message__file-title" v-if="message.file.name">{{ message.file.name }}</div>
            <div class="message__file-size">{{ fileSize }}</div>
          </div>
        </div>
      </template>

      <div class="message__meta">
        <div class="message__time">{{ timestamp }}</div>
        <div v-if="message.read" class="message__seen">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.6666 5L4.3333 12.3333L0.999969 9" stroke="#CCD7E6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
            <path d="M16.6666 5L9.33328 12.3333L8.33328 11.3333" stroke="#CCD7E6" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
      </div>
      <MessageOptionsDropdown
        @click.native.stop
        v-if="messageOptionsDropdown"
        :position="messageOptionsDropdown"
        :isAdmin="isAdmin"
        :message="message"
        @close="closeOptions"
        @select="selectModeHandle"
        @reply="replyMessageHandle"
        @forward="forwardMessageHandle"
        @delete="handleDeleteClick"
        @spam="handleSpamClick"
        @copy="handleCopy"
      />
      <!-- more -->
      <!--      <PopoverMenu class="message__more">-->
      <!--        <a href="#" @click="handleEditClick">{{ $t('chat.message.edit') }}</a>-->
      <!--        <a href="#" @click="handleDeleteClick">{{ $t('chat.message.delete') }}</a>-->
      <!--      </PopoverMenu>-->
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import { timeToHHMM } from '@/utils/date'
import { formatBytes } from '@/utils/bytes'

import { withUi } from '@/components/ui'
import MessageOptionsDropdown from '@/components/chat/MessageOptionsDropdown.vue'
import outsize from '@/mixins/outsize'
import Checkbox from '@/components/form/Checkbox.vue'
import handleErrorWithToast from '@/utils/handleErrorWithToast'

export default {
  components: { Checkbox, MessageOptionsDropdown },
  mixins: [withUi, outsize],
  props: {
    message: Object,
    selected: [Boolean, null],
    selectMode: Boolean,
    isAdmin: Boolean,
  },
  data: () => ({
    messageOptionsDropdown: false,
  }),
  computed: {
    forward() {
      return this.message.forward_of
    },
    quote() {
      return this.message.reply_of
    },
    messageBody() {
      return this.message.content
    },
    isIncoming() {
      const user = this.$auth && this.$auth.user()

      return this.message.author.id === user.id
    },
    isFile() {
      return [2, 3].includes(this.message.type.id)
    },
    fileImageUrl() {
      return this.message.file?.preview || this.message.file?.image
    },
    fileSize() {
      return formatBytes(this.message.file.size)
    },
    timestamp() {
      return timeToHHMM(this.message.created_at)
    },
    // ...mapGetters('auth', ['user']),
  },
  methods: {
    openOptions(e) {
      if (this.selectMode) {
        this.$emit('select')
        return
      }
      e.preventDefault()
      let leftMargin = 12
      if (!this.isIncoming) {
        leftMargin += 24 + 30
      }
      const xClick = Math.min(e.layerX, this.isIncoming ? 100 : 45)
      this.messageOptionsDropdown = { x: xClick + leftMargin, y: e.clientY }
    },
    closeOptions() {
      this.messageOptionsDropdown = false
    },
    replyMessageHandle() {
      this.closeOptions()
      this.$emit('reply', this.message)
    },
    forwardMessageHandle(chat) {
      this.closeOptions()
      this.$emit('forward', {
        message: this.message,
        chat,
      })
    },
    selectModeHandle() {
      this.closeOptions()
      this.$emit('select-mode')
      this.$emit('select', this.message)
    },
    handleEditClick() {
      const { id, content } = this.message

      this.setEditable({
        id,
        content,
      })
    },
    async handleDeleteClick() {
      this.closeOptions()
      await this.deleteMessage({ id: this.message.id }).then(() => {
        this.$emit('delete')
      }, handleErrorWithToast.bind(this))
    },
    handleCopy() {
      navigator.clipboard.writeText(this.message.content)
      this.closeOptions()
    },
    async handleSpamClick() {
      await this.spamMessage({
        id: this.message.id,
        state: !this.message.spam,
      });
      this.closeOptions()
    },
    handleFileClick() {
      if (this.message.type.id !== 2) {
        window.open(this.message.file.image)
      } else {
        window.$viewer = this.$viewerApi({
          options: {
            navbar: false,
            toolbar: false,
            movable: false,
            rotatable: false,
            zoomOnWheel: false,
            title: false,
            button: false,
            zoomable: false,
          },
          images: [this.message.image],
        })
      }
    },
    ...mapMutations('chat', ['setEditable']),
    ...mapActions('chat', ['getMessages', 'editMessage', 'deleteMessage', 'spamMessage']),
  },
}
</script>

<style lang="scss" scoped>
.message {
  margin: 16px 0;
  padding-right: 16px;
  display: flex;
  align-items: center;

  &__select {
    display: none;
    position: absolute;
    top: 12px;
    right: 12px;
  }

  &__wrapper {
    position: relative;
    padding: 12px 24px;
    max-width: 440px;
    min-width: 1px;
    width: 100%;

    &--forward {
      padding: 0 24px 12px 24px !important;
    }

    &.is-file {
      padding: 14px 25px 8px 10px;
    }

    &:hover {
      .message {
        &__more {
          opacity: 1;
        }
      }
    }

    // &.is-file {
    //   .message__more {
    //     display: none;
    //   }
    // }
  }

  &--selected & {
    &__select {
      display: block;
    }

    &__wrapper {
      padding: 30px 24px 12px 24px;
    }
  }

  &__user {
    position: relative;
    z-index: 1;
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    margin-right: 12px;
    border-radius: 5px;
    align-self: flex-end;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__forward {
    background: #F0F3F7;
    margin-left: -24px;
    margin-bottom: 4px;
    padding: 8px 24px;
    width: calc(100% + 48px);
    border-radius: 5px 5px 0 0;

    font-size: 15px;
    font-weight: 400;
    line-height: 23.63px;

    span {
      font-weight: 700;
    }
  }

  &__quote {
    margin-bottom: 8px;
    padding-left: 16px;
    background: #F0F3F7;
    border-left: 2px solid #003882;

    &-name {
      font-size: 15px;
      font-weight: 700;
      line-height: 20px;
      color: #474747;
    }

    &-content {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 255px;

      font-size: 15px;
      font-weight: 400;
      line-height: 23.63px;
    }
  }

  &__content {
    font-size: 15px;
    font-weight: 400;
    line-height: 23.63px;
    color: #474747;
  }

  &__file {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    min-width: 1px;
    cursor: pointer;
    transition: opacity 0.25s ease-in-out;

    &:hover {
      opacity: 0.8;
    }
  }

  &__file-image {
    position: relative;
    z-index: 1;
    flex: 0 0 90px;
    width: 90px;
    height: 90px;
    overflow: hidden;
    border-radius: 5px;
    pointer-events: none;

    img {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__file-icon {
    position: relative;
    z-index: 1;
    flex: 0 0 50px;
    width: 50px;
    height: 50px;
    overflow: hidden;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $primary;
    color: white;

    .svg-icon {
      font-size: 16px;
    }
  }

  &__file-meta {
    padding-left: 16px;
    flex: 0 0 calc(100% - 50px);
    width: calc(100% - 50px);
    height: calc(100% - 50px);
    min-width: 1px;
  }

  &__file-title {
    font-size: 14px;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__file-size {
    margin-top: 2px;
    font-weight: bold;
    font-size: 14px;
    line-height: 140.3%;
  }

  &__meta {
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__time {
    font-size: 12px;
    color: $dark;
    font-weight: 700;
  }

  &__seen {
    margin-left: 4px;
  }

  // more
  &__more {
    position: absolute;
    left: -24px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: opacity 0.25s $ease;

    ::v-deep .more__menu {
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: 3;
      width: 100px;
    }
  }

  &--incoming {
    justify-content: flex-end;

    .message__wrapper {
      color: $darkGrey;
      border: 1px solid #F0F3F7;
      border-radius: 10px 10px 0 10px;
    }

    .message {
      &__user {
        display: none;
      }

      &__time {
        color: $dark;
      }
    }

    // .message__file-icon {
    //   background: white;
    //   color: $primary;
    // }
  }

  &--outcoming {
    justify-content: flex-start;

    .message__wrapper {
      background: #F0F3F7;
      color: $dark;
      border-radius: 10px 10px 10px 0;
    }
  }

  &.is-ghost {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
